import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const IncorporationStructures = () => {
  const [activeTab, setActiveTab] = useState('llc');

  const structures = {
    llc: {
      title: 'LLC',
      subtitle: 'Limited Liability Company',
      sections: [
        {
          heading: 'Overview',
          content: 'LLCs are particularly beneficial for startups because they offer simplicity and protection. If you\'re a sole proprietor or have a small team, an LLC can provide the safety net you need while keeping things straightforward.'
        },
        {
          heading: 'Considerations',
          content: 'If your startup aims to attract investors, forming an LLC might complicate things due to certain investment restrictions. Similarly, nonprofits often find the LLC structure too complex, given the stringent IRS conditions and varying state laws.'
        },
        {
          heading: 'Steps to Startup',
          items: [
            'Name Your LLC',
            'Provide an Address',
            'Assign a Registered Agent',
            'Provide Names and Addresses of LLC Members',
            'State the Purpose of Your LLC',
            'File the Articles of Organization'
          ]
        }
      ]
    },
    scorp: {
      title: 'US S-Corp',
      subtitle: 'S Corporation',
      sections: [
        {
          heading: 'S Corp Formation & Functioning',
          items: [
            'Register the business as a corporation with the state.',
            'File IRS Form 2553 to elect S corp status (if eligible).'
          ]
        },
        {
          heading: 'Ownership & Management',
          items: [
            'Shareholders own the company through shares.',
            'Shareholders elect a Board of Directors.',
            'The Board appoints officers for daily operations.'
          ]
        },
        {
          heading: 'Profit Distribution',
          items: [
            'Profits distributed via salaries, distributions, or both.',
            'Shareholder-employees must receive reasonable salaries (subject to payroll taxes).',
            'Distributions may be subject to individual income tax.'
          ]
        },
        {
          heading: 'Pass-Through Taxation',
          items: [
            'No federal corporate income tax at the entity level.',
            'Profits and losses pass through to shareholders.',
            'Shareholders report income, deductions, and credits on personal returns.',
            'Taxed at individual income tax rates.',
            'Losses may offset other personal income (subject to limits).'
          ]
        },
        {
          heading: 'Limited Liability',
          items: [
            'Shareholders have limited liability protection.',
            'Personal assets are generally protected from business debts and liabilities.'
          ]
        }
      ]
    },
    ccorp: {
      title: 'US C-Corp',
      subtitle: 'C Corporation',
      sections: [
        {
          heading: 'Key Features',
          items: [
            'Strong separation of personal and business assets.',
            'Owners\' personal assets protected from business liabilities.',
            'Perpetual existence (continues despite ownership changes).'
          ]
        },
        {
          heading: 'Ownership Flexibility',
          items: [
            'Unlimited shareholders.',
            'Shareholders can be from anywhere globally.',
            'Multiple classes of stock allowed.',
            'Other businesses can own shares.',
            'Preferred structure for outside investors.'
          ]
        },
        {
          heading: 'Tax Structure',
          items: [
            'Subject to entity-level taxation.',
            'Corporation pays tax on profits.',
            'Shareholders may pay tax again on dividends (double taxation).'
          ]
        },
        {
          heading: 'Best Suited For',
          items: [
            'High-growth businesses.',
            'Venture capital-backed companies.',
            'Companies planning IPOs.',
            'Businesses retaining earnings for expansion.',
            'Companies offering stock options and robust benefits.'
          ]
        }
      ]
    }
  };

  const renderSection = (section, index) => {
    if (section.content) {
      return (
        <div key={index} className="mb-6">
          <h4 className="font-semibold text-[#3E2723] mb-2 text-lg">{section.heading}</h4>
          <p className="text-gray-600 leading-relaxed">{section.content}</p>
        </div>
      );
    }
    if (section.items) {
      return (
        <div key={index} className="mb-6">
          <h4 className="font-semibold text-[#3E2723] mb-3 text-lg">{section.heading}</h4>
          <ul className="space-y-2">
            {section.items.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-[#8D6E63] mr-3 mt-1.5 w-1.5 h-1.5 bg-[#8D6E63] rounded-full flex-shrink-0"></span>
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  return (
    <section className="py-20 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
          Choose Your Entity Structure
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Select the right business structure for your needs
        </p>

        {/* Tab Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Button
            onClick={() => setActiveTab('llc')}
            className={`px-8 py-3 rounded-lg font-medium transition-all ${activeTab === 'llc' ? 'bg-[#3E2723] text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
          >
            LLC
          </Button>
          <Button
            onClick={() => setActiveTab('scorp')}
            className={`px-8 py-3 rounded-lg font-medium transition-all ${activeTab === 'scorp' ? 'bg-[#3E2723] text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
          >
            US S-Corp
          </Button>
          <Button
            onClick={() => setActiveTab('ccorp')}
            className={`px-8 py-3 rounded-lg font-medium transition-all ${activeTab === 'ccorp' ? 'bg-[#3E2723] text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
          >
            US C-Corp
          </Button>
        </div>

        {/* Structure Details */}
        <Card className="border border-stone-200 shadow-lg bg-white">
          <CardContent className="p-8 md:p-10">
            <div className="mb-8 pb-6 border-b border-stone-100">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{structures[activeTab].title}</h3>
              <p className="text-[#8D6E63] font-medium text-lg">{structures[activeTab].subtitle}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
              {structures[activeTab].sections.map((section, index) => (
                <div key={index} className={structures[activeTab].sections.length <= 3 ? 'md:col-span-2' : ''}>
                  {renderSection(section, index)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default IncorporationStructures;
